import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Group } from './Group.js';

const MAX_DEPTH = 10;

/**
 * Recursively resolves a group from a root group by traversing parent groups until a matching group is found.
 * @param rootGroup The root group to start the search from.
 * @param comparator A function that can be used to compare the parent group with anything to determine if it is the right group. Also receives the root group as the second argument.
 * @returns The matching group or null if no match is found.
 */
export async function recursiveGroupResolver<T extends Group>(
  rootGroup: Group,
  comparator: (comparedGroup: EntityData<Group>, rootGroup: EntityData<Group>) => boolean,
): Promise<T | null> {
  const groupCol = await getRawCollection<EntityData<Group>>(Group._collectionName);

  /**
   * Early stopping if the group would reference itself. For example if we are searching schools in the parents and are already on a school group.
   */
  if (comparator(rootGroup, rootGroup)) {
    return rootGroup as T;
  }

  /**
   * Save what groups we already visited to prevent infinite loops or checking things multiple times
   */
  const visitedGroupIds = new Set<string>();
  if (rootGroup._id) visitedGroupIds.add(rootGroup._id);

  let foundGroup: T | null = null;
  let depth = 0;
  let parentGroupIds: string[] = rootGroup.parentIDs ?? [];

  while (depth < MAX_DEPTH && !foundGroup) {
    /**
     * No parents are found, we can't go further
     */
    if (!parentGroupIds.length) {
      break;
    }
    /**
     * Get all parents
     */
    const parentGroups = await groupCol.find({ _id: { $in: parentGroupIds } }).toArray();

    /**
     * Search for a fitting group and return it
     */
    const possibleFoundSchool = parentGroups.find(group => comparator(group, rootGroup)) ?? null;
    if (possibleFoundSchool) {
      foundGroup = possibleFoundSchool as T;
      break;
    }

    /**
     * Prepare for the next iteration.
     * Increase depth and add all searched groups to the visited set
     */
    depth++;
    for (const parentGroupId of parentGroupIds) {
      visitedGroupIds.add(parentGroupId);
    }
    /**
     * For next iteration flatmap all parents from the visited parents and filter out the already visited ones
     */
    parentGroupIds = parentGroups
      .flatMap(group => group.parentIDs)
      .filter(groupId => groupId && !visitedGroupIds.has(groupId)) as string[];
  }

  return foundGroup ? constructValue(foundGroup) : null;
}
